<template>
  <div class="main-content">
    <div class="container">
      <!--<div class="header-section">
        <h1>CoExCell Tutorial</h1>
        <p class="lead">Follow this guide to explore and analyze single-cell, spatial, and gene co-expression data.</p>
      </div>-->

      <div class="panel">
        <el-row :gutter="40" type="flex">
          <!-- 左侧导航 -->
          <el-col :span="6">
            <div class="sidebar-fixed-wrapper">
              <div class="sidebar-title">Contents</div>
              <nav class="sidebar">
                <ul>
                  <li>
                    <a href="#" :class="{ active: activeSection === 'single-cell' }" @click.prevent="scrollTo('single-cell')">
                      Single Cell/Spatial Data
                    </a>
                  </li>
                  <li>
                    <a href="#" :class="{ active: activeSection === 'gene' }" @click.prevent="scrollTo('gene')">
                      Gene
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          </el-col>

          <!-- 正文 -->
          <el-col :span="18">
            <div class="tutorial">
              <!-- Single Cell/Spatial Data -->
              <section id="single-cell">
                <h2>Single Cell/Spatial Data</h2>
                <div class="content-block">
                  <p>
                    To explore single-cell data in <strong>CoExCell</strong>, begin by browsing tissues using the search box on the left.
                  </p>
                  <p>
                    Once a tissue is selected, all available datasets for that tissue will be displayed. Alternatively, you can enter a tissue keyword into the search field and click Search to directly retrieve the corresponding single-cell datasets.
                  </p>
                  <figure>
                    <img src="/images/tutorial/img1.png" alt="Dataset Selection" />
                  </figure>

                  <p>
                    Select Gene subsection to explore the genes coexpression table.
                  </p>
                  <figure>
                    <img src="/images/tutorial/img2.png" alt="Gene Subsection" />
                  </figure>

                  <p>
                    In gene centric subsection. A summary table enumerates all gene-gene paired correlation with detailed information is also provided. Users can filter by adjusted p value, correlation coefficients, cell type, gene1 and gene2 to query their interested gene pairs in interested cell types.
                  </p>
                  <figure>
                    <img src="/images/tutorial/img3.png" alt="Gene centric subsection" />
                  </figure>

                  <p>
                    In module centric subsection, a table with all the modules in this dataset is provided. Click the module name to see detailed information of the modules.
                  </p>
                  <figure>
                    <img src="/images/tutorial/img4.png" alt="Module centric subsection" />
                  </figure>

                  <p>
                    Detailed pathway and transcription factor (TF) enrichment results are provided in the accompanying table, which includes downloadable links.
                  </p>
                  <figure>
                    <img src="/images/tutorial/img5.png" alt="Enrichment Results" />
                  </figure>
                </div>
              </section>

              <!-- Gene -->
              <section id="gene">
                <h2>Gene</h2>
                <div class="content-block">
                  <p>
                    Select an organism. Then, search and click a gene to view its co-expression patterns and module affiliations.
                  </p>
                  <figure>
                    <img src="/images/tutorial/img6.png" alt="Gene Search" />
                  </figure>

                  <p>
                    View genes coexpressed with selected genes in the gene subpage.
                  </p>
                  <figure>
                    <img src="/images/tutorial/img7.png" alt="Coexpressed Genes" />
                  </figure>

                  <p>
                    View the modules to which the gene contributes on the module details page.
                  </p>
                  <figure>
                    <img src="/images/tutorial/img8.png" alt="Module Contributions" />
                  </figure>
                </div>
              </section>
            </div>
          </el-col>
        </el-row>
      </div>
      <div style="margin-bottom: 40px;"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: "TutorialPage",
  data() {
    return {
      activeSection: "single-cell"
    };
  },
  mounted() {
    window.addEventListener("scroll", this.onScroll);
    this.onScroll();
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.onScroll);
  },
  methods: {
    scrollTo(id) {
      const el = document.getElementById(id);
      if (el) {
        window.scrollTo({
          top: el.offsetTop - 120,
          behavior: "smooth"
        });
      }
    },
    onScroll() {
      const sections = document.querySelectorAll("section");
      if (sections.length === 0) return;

      const scrollY = window.scrollY + 200;
      let current = sections[0].id;

      sections.forEach((section) => {
        if (scrollY >= section.offsetTop) {
          current = section.getAttribute("id");
        }
      });

      // 处理滚动到底部
      const scrolledToBottom = (window.innerHeight + window.scrollY) >= document.body.scrollHeight - 5;
      if (scrolledToBottom) {
        current = sections[sections.length - 1].id;
      }

      this.activeSection = current;
    }
  }
};
</script>

<style scoped>
.main-content {
  background-color: var(--bg);
  min-height: 100vh;
  padding: 30px 0;
  font-family: 'Inter', sans-serif;
  color: var(--navy);
}

.container {
  padding: 0 20px;
  max-width: 1400px;
  margin: auto;
  padding-bottom: 5px;
}

.header-section {
  margin-bottom: 30px;
  text-align: center;
}

h1 {
  font-family: 'Playfair Display', serif;
  font-size: 42px;
  font-weight: 700;
  color: var(--navy);
  margin: 0 0 10px 0;
}

.lead {
  color: var(--muted);
  font-size: 18px;
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
}

.panel {
  background: linear-gradient(180deg, #f9fbfe 0%, #d8e1ee 100%);
  border: 1px solid var(--border);
  border-radius: 20px;
  padding: 40px;
  padding-bottom: 0px;
  box-shadow: var(--shadow);
}

/* 左侧导航栏 */
.sidebar-fixed-wrapper {
  position: sticky;
  top: 40px;
  display: flex;
  flex-direction: column;
}

.sidebar-title {
  font-family: 'Playfair Display', serif;
  font-size: 22px;
  font-weight: 600;
  color: var(--navy);
  margin-bottom: 20px;
  border-bottom: 2px solid var(--accent);
  padding-bottom: 8px;
  display: inline-block;
}

.sidebar {
  background: rgba(255, 255, 255, 0.6);
  border-radius: 12px;
  padding: 10px;
  border: 1px solid var(--border);
}

.sidebar ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.sidebar a {
  display: block;
  padding: 12px 16px;
  border-radius: 8px;
  text-decoration: none;
  color: var(--muted);
  font-weight: 500;
  transition: all 0.3s ease;
  font-size: 15px;
}

.sidebar a:hover {
  color: var(--navy);
  background: rgba(93, 135, 181, 0.1);
}

.sidebar a.active {
  color: #fff;
  background: var(--accent);
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(93, 135, 181, 0.3);
}

/* 正文 */
.tutorial {
  line-height: 1.8;
  font-size: 16px;
  color: var(--navy);
}

.tutorial h2 {
  font-family: 'Playfair Display', serif;
  font-size: 28px;
  color: var(--navy);
  margin-bottom: 25px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(199, 209, 221, 0.5);
}

.content-block {
  background: rgba(255, 255, 255, 0.4);
  padding: 30px;
  padding-top: 0px;
  border-radius: 15px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  margin-bottom: 40px;
}

section {
  margin-bottom: 50px;
}

figure {
  text-align: center;
  margin: 35px 0;
}

figure img {
  max-width: 100%;
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  transition: transform 0.3s ease;
}

figure img:hover {
  transform: scale(1.01);
}

p {
  margin-bottom: 20px;
  color: var(--muted);
}

strong {
  color: var(--navy);
  font-weight: 700;
}
</style>
